import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BottomNav from "@/components/feature/BottomNav";
import OrderReviewModal from "@/components/feature/OrderReviewModal";
import {
  goodsApi,
  orderApi,
  type AppendGoodsItem,
  type OrderAppendItem,
  type OrderAppendSettlementResult,
  type OrderListItem,
  type OrderPayWayItem,
} from "@/api";
import { resolveCustomerAssetUrl } from "@/lib/asset";
import { auth } from "@/lib/auth";
import { CUSTOMER_ORDER_NOTIFY_EVENT, type AppNotifyMessage } from "@/lib/app-notify";
import { buildLoginRedirect } from "@/lib/navigation";
import { RequestError } from "@/lib/request";
import {
  invokeWechatJsapiPay,
  isWechatJsapiPayConfig,
  isWechatPayAlreadyPaid,
  waitForWechatPayResultReady,
} from "@/lib/wechat-pay";

type OrderStatus =
  | "all"
  | "pending_pay"
  | "on_the_way"
  | "arrived"
  | "in_progress"
  | "completed"
  | "pending_review"
  | "cancelled";

type PayMethod = number | "wechat" | "balance";

type PayFeedback = {
  type: "balance" | "payment";
  title: string;
  message: string;
} | null;

interface Order {
  id: string;
  rawId: number;
  rawStatus: number;
  createTime: string;
  service: string;
  therapist: string;
  therapistAvatar: string;
  therapistPhone: string;
  therapistPhoneMasked: string;
  qty: number;
  total: number;
  status: OrderStatus;
  statusLabel: string;
  subStatus?: string;
  image: string;
  address: string;
  duration: string;
  serviceTime: string;
  note: string;
  originalPrice: number;
  coupon: number;
  travel: number;
  travelKm: string;
  payMethod: string;
  orderNo: string;
  payTime: string;
  countdown?: number;
  cancelReason?: string;
  cancelDetail?: string;
  cancelTime?: string;
  arrivalMinutes?: number;
  canPay: boolean;
  canCancel: boolean;
  goodsId: number;
  orderGoodsId: number;
  commentId?: number | null;
  canComment: boolean;
  canViewComment: boolean;
  canFinishEarly: boolean;
  canAppend: boolean;
  appendCount: number;
  appendDuration: number;
  appendAmount: number;
  appendEstimatedFinishTime: string;
  appendRecords: OrderAppendItem[];
  pendingAppendRecords: OrderAppendItem[];
  pendingAppendCount: number;
  pendingAppendAmount: number;
  canPayPendingAppend: boolean;
}

const TABS: { key: OrderStatus | "all"; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "in_progress", label: "进行中" },
  { key: "completed", label: "已完成" },
  { key: "pending_review", label: "待评价" },
  { key: "cancelled", label: "已取消" },
];

const STATUS_THEME: Record<
  Exclude<OrderStatus, "all">,
  {
    gradient: string;
    glowColor: string;
    icon: string;
    accentColor: string;
    tagBg: string;
    tagColor: string;
    tagDot?: string;
    tagLabel: string;
  }
> = {
  pending_pay: {
    gradient: "linear-gradient(135deg, #FFF5ED 0%, #FFE8D6 100%)",
    glowColor: "#FFCCA0",
    icon: "ri-time-line",
    accentColor: "#E8600A",
    tagBg: "#FFF0E6",
    tagColor: "#E8600A",
    tagDot: "#FF7B22",
    tagLabel: "待支付",
  },
  on_the_way: {
    gradient: "linear-gradient(135deg, #FFF8EC 0%, #FFEECF 100%)",
    glowColor: "#FFD890",
    icon: "ri-motorbike-line",
    accentColor: "#CC7800",
    tagBg: "#FFF5E0",
    tagColor: "#CC7800",
    tagDot: "#F0A020",
    tagLabel: "进行中",
  },
  arrived: {
    gradient: "linear-gradient(135deg, #FFF8EC 0%, #FFEECF 100%)",
    glowColor: "#FFD890",
    icon: "ri-map-pin-2-line",
    accentColor: "#CC7800",
    tagBg: "#FFF5E0",
    tagColor: "#CC7800",
    tagDot: "#F0A020",
    tagLabel: "已到达",
  },
  in_progress: {
    gradient: "linear-gradient(135deg, #EDFBF5 0%, #D0F4E6 100%)",
    glowColor: "#90E8C0",
    icon: "ri-service-line",
    accentColor: "#0A9060",
    tagBg: "#E4F8EF",
    tagColor: "#0A9060",
    tagDot: "#17B978",
    tagLabel: "服务中",
  },
  completed: {
    gradient: "linear-gradient(135deg, #F0FAF5 0%, #D8F2E8 100%)",
    glowColor: "#A0DCC0",
    icon: "ri-checkbox-circle-line",
    accentColor: "#1A9068",
    tagBg: "#E8F7EF",
    tagColor: "#1A9068",
    tagLabel: "已完成",
  },
  pending_review: {
    gradient: "linear-gradient(135deg, #FFFBEA 0%, #FFF3C4 100%)",
    glowColor: "#FFE080",
    icon: "ri-star-line",
    accentColor: "#B08000",
    tagBg: "#FFF8DC",
    tagColor: "#B08000",
    tagDot: "#E0A800",
    tagLabel: "待评价",
  },
  cancelled: {
    gradient: "linear-gradient(135deg, #F5F5F7 0%, #EBEBED 100%)",
    glowColor: "#D8D8DC",
    icon: "ri-close-circle-line",
    accentColor: "#AAAAAA",
    tagBg: "#F0F0F0",
    tagColor: "#AAAAAA",
    tagLabel: "已取消",
  },
};

const DEFAULT_THERAPIST_AVATAR =
  "https://readdy.ai/api/search-image?query=young%20asian%20massage%20therapist%20portrait%20professional%20uniform%20clean%20background&width=120&height=120&seq=customer-order-avatar&orientation=squarish";
const DEFAULT_ORDER_IMAGE =
  "https://readdy.ai/api/search-image?query=massage%20service%20spa%20professional%20indoors%20warm%20light%20clean%20high%20quality&width=240&height=240&seq=customer-order-goods&orientation=squarish";
const ORDER_TIMEOUT_SECONDS = 30 * 60;

function toNumber(value?: number | string | null, fallback = 0) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : fallback;
}

function parseDateTime(raw?: string) {
  if (!raw) {
    return null;
  }
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getCountdownSeconds(createTime?: string) {
  const createdAt = parseDateTime(createTime);
  if (!createdAt) {
    return undefined;
  }
  const elapsed = Math.floor((Date.now() - createdAt.getTime()) / 1000);
  return Math.max(0, ORDER_TIMEOUT_SECONDS - elapsed);
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message.trim()) {
    const message = error.message.trim();
    const mappedMessages: Record<string, string> = {
      "Coach is unavailable during append time": "技师该加钟时段不可服务，请选择其他加钟项目或稍后再试",
      "Coach has another order during append time": "技师该加钟时段已有其他订单，请选择其他加钟项目或稍后再试",
      "Append time is outside goods service window": "加钟时间超出项目可服务时段",
      "Append service time is invalid": "加钟服务时长配置异常",
      "Goods is not an append goods": "请选择可加钟项目",
      "Append goods duration is not configured": "加钟项目未配置服务时长",
    };
    return mappedMessages[message] || message;
  }
  return fallback;
}

function isBalanceInsufficientMessage(message: string) {
  const normalized = message.toLowerCase();
  return message.includes("余额不足") || normalized.includes("balance") || normalized.includes("insufficient");
}

function payWayIcon(payWay: number) {
  if (payWay === 1) {
    return "ri-wechat-line";
  }
  if (payWay === 2) {
    return "ri-alipay-line";
  }
  if (payWay === 3) {
    return "ri-wallet-3-line";
  }
  return "ri-bank-card-line";
}

function payWayColor(payWay: number) {
  if (payWay === 1) {
    return "#07C160";
  }
  if (payWay === 2) {
    return "#1677FF";
  }
  if (payWay === 3) {
    return "#FF8C3A";
  }
  return "#5A6BFF";
}

function defaultPayWay(payWays: OrderPayWayItem[]) {
  return payWays.find((item) => item.pay_way === 1)?.pay_way ?? payWays.find((item) => item.is_default === 1)?.pay_way ?? payWays[0]?.pay_way ?? 1;
}

function filterAppendPayWays(payWays: OrderPayWayItem[]) {
  return payWays.filter((item) => item.pay_way !== 2);
}

function normalizePayWay(method: PayMethod) {
  if (method === "wechat") {
    return 1;
  }
  if (method === "balance") {
    return 3;
  }
  return method;
}

function joinAddressParts(addressSnap?: Record<string, unknown>) {
  if (!addressSnap) {
    return "";
  }
  const parts = [
    addressSnap.province,
    addressSnap.city,
    addressSnap.district,
    addressSnap.address,
    addressSnap.house_number,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
  return parts.join("");
}

function formatDistance(distance?: string) {
  const raw = String(distance ?? "").trim();
  if (!raw) {
    return "-";
  }
  if (raw.includes("km") || raw.includes("m")) {
    return raw;
  }
  const numeric = Number(raw);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return "-";
  }
  return `${numeric.toFixed(numeric >= 100 ? 0 : 2)}km`;
}

function maskPhone(phone?: string) {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (digits.length < 7) {
    return digits;
  }
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}****${digits.slice(-4)}`;
  }
  return `${digits.slice(0, 3)}***${digits.slice(-3)}`;
}

function buildServiceTime(order: OrderListItem) {
  if (order.appoint_time_desc) {
    return order.appoint_time_desc;
  }
  const parts = [order.appoint_date, order.appoint_time].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "-";
}

function buildDuration(order: OrderListItem) {
  const totalDuration = (order.order_goods || []).reduce((sum, item) => {
    return sum + toNumber(item.duration) * Math.max(1, toNumber(item.goods_num, 1));
  }, 0);
  return totalDuration > 0 ? `${totalDuration}分钟` : "-";
}

function appendRecordDuration(record: OrderAppendItem) {
  return toNumber(record.total_duration) || toNumber(record.duration) * Math.max(1, toNumber(record.goods_num, 1));
}

function cancelTravelFee(order: Order) {
  if (order.rawStatus === 3 || order.rawStatus === 4) {
    return Math.max(0, order.travel);
  }
  return 0;
}

function canDeleteOrder(order: Order) {
  return order.status === "cancelled" || order.status === "completed" || order.status === "pending_review";
}

function resolveOrderStatus(order: OrderListItem): OrderStatus {
  const rawStatus = toNumber(order.order_status);
  if (rawStatus === 0) {
    return "pending_pay";
  }
  if (rawStatus === 6) {
    return order.comment_btn === 1 ? "pending_review" : "completed";
  }
  if (rawStatus >= 7) {
    return "cancelled";
  }
  if (rawStatus === 4) {
    return "arrived";
  }
  if (rawStatus === 5) {
    return "in_progress";
  }
  return "on_the_way";
}

function resolveSubStatus(rawStatus: number) {
  if (rawStatus === 1) {
    return "已支付，等待技师接单";
  }
  if (rawStatus === 2) {
    return "技师已接单，等待出发";
  }
  if (rawStatus === 3) {
    return "技师正在上门途中";
  }
  if (rawStatus === 4) {
    return "技师已到达";
  }
  if (rawStatus === 5) {
    return "服务进行中";
  }
  return "";
}

function mapApiOrderToView(order: OrderListItem): Order {
  const goods = order.order_goods?.[0];
  const rawStatus = toNumber(order.order_status);
  const status = resolveOrderStatus(order);
  const total = toNumber(order.total_order_amount ?? order.order_amount ?? order.total_amount);
  const address = joinAddressParts(order.address_snap);
  const appendRecords = Array.isArray(order.order_append) ? order.order_append : [];
  const pendingAppendRecords = Array.isArray(order.pending_append) ? order.pending_append : [];
  const appendDuration =
    toNumber(order.total_append_duration) || appendRecords.reduce((sum, record) => sum + appendRecordDuration(record), 0);
  const appendAmount =
    toNumber(order.total_append_amount) || appendRecords.reduce((sum, record) => sum + toNumber(record.order_amount), 0);
  const appendCount = toNumber(order.append_count) || appendRecords.length;
  const pendingAppendAmount =
    toNumber(order.pending_append_amount) || pendingAppendRecords.reduce((sum, record) => sum + toNumber(record.order_amount), 0);
  const pendingAppendCount = toNumber(order.pending_append_count) || pendingAppendRecords.length;

  return {
    id: String(order.id),
    rawId: toNumber(order.id),
    rawStatus,
    createTime: String(order.create_time ?? ""),
    service: String(goods?.goods_name ?? "服务项目"),
    therapist: String(order.coach_info?.name ?? "待分配技师"),
    therapistAvatar:
      resolveCustomerAssetUrl(String(order.coach_info?.work_photo ?? "")) || DEFAULT_THERAPIST_AVATAR,
    therapistPhone: String(order.coach_info?.mobile ?? "").trim(),
    therapistPhoneMasked: maskPhone(order.coach_info?.mobile),
    qty: Math.max(1, toNumber(goods?.goods_num, 1)),
    total,
    status,
    statusLabel: String(order.order_status_desc ?? ""),
    subStatus: resolveSubStatus(rawStatus),
    image: resolveCustomerAssetUrl(goods?.goods_image) || DEFAULT_ORDER_IMAGE,
    address: address || "暂无服务地址",
    duration: buildDuration(order),
    serviceTime: buildServiceTime(order),
    note: String(order.user_remark ?? ""),
    originalPrice: toNumber(order.goods_price ?? goods?.goods_price ?? total),
    coupon: toNumber(order.discount_amount),
    travel: toNumber(order.car_amount),
    travelKm: formatDistance(order.order_distance),
    payMethod: String(order.pay_way_desc ?? "-"),
    orderNo: String(order.sn ?? ""),
    payTime:
      toNumber(order.pay_status) === 1
        ? String(order.pay_time ?? order.create_time ?? "-")
        : "-",
    countdown: rawStatus === 0 ? getCountdownSeconds(order.create_time) : undefined,
    cancelReason: rawStatus >= 7 ? "订单已取消" : undefined,
    cancelDetail:
      rawStatus >= 7 ? "当前订单已关闭，如已支付退款，将按原路退回。" : undefined,
    cancelTime:
      rawStatus >= 7
        ? String(order.cancel_time ?? order.pay_time ?? order.create_time ?? "-")
        : undefined,
    arrivalMinutes: rawStatus === 3 ? toNumber(order.estimated_arrival_minutes) || undefined : undefined,
    canPay: order.pay_btn === 1,
    canCancel: order.user_cancel_btn === 1 || order.cancel_btn === 1,
    orderGoodsId: toNumber(goods?.id),
    commentId: goods?.comment_id ?? null,
    canComment: order.comment_btn === 1 && toNumber(goods?.id) > 0,
    canViewComment: order.look_comment_btn === 1 && toNumber(goods?.comment_id) > 0,
    goodsId: toNumber(goods?.goods_id),
    canFinishEarly: rawStatus === 5,
    canAppend: rawStatus === 5 && order.append_btn === 1 && toNumber(goods?.goods_id) > 0,
    appendCount,
    appendDuration,
    appendAmount,
    appendEstimatedFinishTime: String(order.estimated_finish_time ?? ""),
    appendRecords,
    pendingAppendRecords,
    pendingAppendCount,
    pendingAppendAmount,
    canPayPendingAppend: pendingAppendCount > 0 && order.pending_append_pay_btn === 1,
  };
}

function matchesTab(order: Order, tab: OrderStatus | "all") {
  if (tab === "all") {
    return true;
  }
  if (tab === "in_progress") {
    return order.status === "on_the_way" || order.status === "arrived" || order.status === "in_progress";
  }
  return order.status === tab;
}

function countByTab(orders: Order[], tab: OrderStatus | "all") {
  return orders.filter((item) => matchesTab(item, tab)).length;
}

function CountdownText({ initialSeconds }: { initialSeconds: number }) {
  const [seconds, setSeconds] = useState(initialSeconds);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setSeconds((current) => (current > 0 ? current - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  const minuteText = String(Math.floor(seconds / 60)).padStart(2, "0");
  const secondText = String(seconds % 60).padStart(2, "0");

  return (
    <span className="font-bold" style={{ color: "#E8600A" }}>
      {minuteText}:{secondText}
    </span>
  );
}

function CancelReasonModal({ order, onClose }: { order: Order; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl bg-white px-5 pb-8 pt-5"
        style={{ maxWidth: 480, animation: "slideUp 0.25s ease" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex min-w-0 items-center justify-between gap-3">
          <span className="text-base font-bold" style={{ color: "#1A1A1A" }}>
            取消原因
          </span>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center cursor-pointer">
            <i className="ri-close-line text-xl" style={{ color: "#999" }} />
          </button>
        </div>

        <div
          className="mb-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
          style={{ background: "#FFF0F0", border: "1px solid #FFCCCC" }}
        >
          <i className="ri-information-line text-sm" style={{ color: "#FF4D4D" }} />
          <span className="text-sm font-semibold" style={{ color: "#FF4D4D" }}>
            {order.cancelReason}
          </span>
        </div>

        <div
          className="rounded-2xl p-4"
          style={{ background: "#F9F9F9", border: "1px solid #F0F0F0" }}
        >
          <p className="text-sm leading-relaxed" style={{ color: "#666" }}>
            {order.cancelDetail}
          </p>
        </div>

        <div className="mb-5 mt-4 flex min-w-0 items-center gap-2">
          <i className="ri-time-line text-sm" style={{ color: "#BBBBBB" }} />
          <span className="min-w-0 flex-1 break-words text-xs" style={{ color: "#BBBBBB" }}>
            取消时间：{order.cancelTime}
          </span>
        </div>

        <button
          onClick={onClose}
          className="w-full rounded-full py-3.5 text-sm font-bold text-white cursor-pointer whitespace-nowrap"
          style={{ background: "linear-gradient(90deg, #FF8C3A 0%, #FF5C00 100%)" }}
        >
          我知道了
        </button>
      </div>
    </div>
  );
}

function PayMethodModal({
  order,
  onClose,
  onPay,
  paying,
  initialMethod,
}: {
  order: Order;
  onClose: () => void;
  onPay: (method: PayMethod) => void;
  paying: boolean;
  initialMethod?: PayMethod;
}) {
  const [payWays, setPayWays] = useState<OrderPayWayItem[]>([]);
  const [selected, setSelected] = useState<PayMethod>(initialMethod ?? 1);
  const [loadingPayWays, setLoadingPayWays] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoadingPayWays(true);
    setError("");
    orderApi
      .getPayWays(order.rawId)
      .then((result) => {
        if (cancelled) {
          return;
        }
        const lists = result.lists ?? [];
        setPayWays(lists);
        setSelected(initialMethod ?? defaultPayWay(lists));
        if (lists.length === 0) {
          setError("暂无可用支付方式");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getErrorMessage(err, "支付方式加载失败，请稍后重试"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingPayWays(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [initialMethod, order.rawId]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl bg-white px-5 pb-8 pt-5"
        style={{ maxWidth: 480, animation: "slideUp 0.25s ease" }}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="mb-5 text-center text-base font-bold" style={{ color: "#1A1A1A" }}>
          请选择支付方式
        </h3>

        <div className="mb-5">
          {loadingPayWays ? (
            <p className="py-4 text-center text-sm" style={{ color: "#999" }}>
              加载中...
            </p>
          ) : error ? (
            <p className="py-4 text-center text-sm" style={{ color: "#D94A00" }}>
              {error}
            </p>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-1">
              {payWays.map((item) => {
                const active = normalizePayWay(selected) === item.pay_way;
                return (
                  <button
                    key={item.id || item.pay_way}
                    onClick={() => setSelected(item.pay_way)}
                    className="flex min-w-[8.5rem] flex-shrink-0 items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold"
                    style={{
                      border: active ? "1.5px solid #FF7B22" : "1px solid #E5E5E5",
                      color: active ? "#FF5C00" : "#666",
                      background: active ? "#FFF7F0" : "#fff",
                    }}
                  >
                    <i
                      className={`${payWayIcon(item.pay_way)} text-base`}
                      style={{ color: active ? "#FF5C00" : payWayColor(item.pay_way) }}
                    />
                    <span className="truncate">{item.name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <button
          onClick={() => onPay(selected)}
          disabled={paying || loadingPayWays || payWays.length === 0}
          className="w-full rounded-full py-3.5 text-base font-bold text-white cursor-pointer whitespace-nowrap"
          style={{
            background:
              paying || loadingPayWays || payWays.length === 0
                ? "#E0E0E0"
                : "linear-gradient(90deg, #FF8C3A, #FF5C00)",
            color: paying || loadingPayWays || payWays.length === 0 ? "#BBBBBB" : "#fff",
          }}
        >
          {paying ? "支付中..." : "立即支付"}
        </button>
      </div>
    </div>
  );
}

function PayFeedbackModal({
  feedback,
  onClose,
  onSwitchWechat,
}: {
  feedback: NonNullable<PayFeedback>;
  onClose: () => void;
  onSwitchWechat: () => void;
}) {
  const isBalance = feedback.type === "balance";

  return (
    <div
      className="fixed inset-0 z-[92] flex items-center justify-center px-6"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="w-full overflow-hidden rounded-3xl bg-white"
        style={{ maxWidth: 360, boxShadow: "0 24px 60px rgba(0,0,0,0.18)", animation: "fadeUp 0.22s ease" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-6 pb-5 pt-7 text-center">
          <div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl"
            style={{ background: isBalance ? "#FFF4EC" : "#FFF0F0", color: isBalance ? "#FF6018" : "#D94A00" }}
          >
            <i className={`${isBalance ? "ri-wallet-3-line" : "ri-error-warning-line"} text-2xl`} />
          </div>
          <p className="text-lg font-bold" style={{ color: "#1A1A1A" }}>{feedback.title}</p>
          <p className="mx-auto mt-2 max-w-[260px] text-sm leading-relaxed" style={{ color: "#777" }}>
            {feedback.message}
          </p>
        </div>

        {isBalance ? (
          <div className="px-5 pb-5">
            <button
              type="button"
              onClick={onSwitchWechat}
              className="mb-3 w-full cursor-pointer whitespace-nowrap rounded-2xl py-3.5 text-sm font-bold text-white"
              style={{ background: "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
            >
              换微信支付
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full cursor-pointer whitespace-nowrap rounded-2xl py-3.5 text-sm font-bold"
              style={{ background: "#FFF4EC", color: "#FF6018" }}
            >
              我知道了
            </button>
          </div>
        ) : (
          <div className="px-5 pb-5">
            <button
              type="button"
              onClick={onClose}
              className="w-full cursor-pointer whitespace-nowrap rounded-2xl py-3.5 text-sm font-bold text-white"
              style={{ background: "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
            >
              我知道了
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function AppendOrderModal({
  order,
  actionLoading,
  onClose,
  onPay,
}: {
  order: Order;
  actionLoading: boolean;
  onClose: () => void;
  onPay: (order: Order, appendGoodsId: number, goodsNum: number, method: PayMethod) => Promise<void>;
}) {
  const [appendGoods, setAppendGoods] = useState<AppendGoodsItem[]>([]);
  const [selectedAppendGoodsId, setSelectedAppendGoodsId] = useState(0);
  const [goodsNum, setGoodsNum] = useState(1);
  const [payMethod, setPayMethod] = useState<PayMethod>(1);
  const [payWays, setPayWays] = useState<OrderPayWayItem[]>([]);
  const [loadingPayWays, setLoadingPayWays] = useState(false);
  const [settlement, setSettlement] = useState<OrderAppendSettlementResult | null>(null);
  const [loadingGoods, setLoadingGoods] = useState(false);
  const [settling, setSettling] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoadingGoods(true);
    setError("");
    goodsApi
      .getAppendGoodsList({ pageNum: 1, pageSize: 50 })
      .then((result) => {
        if (cancelled) {
          return;
        }
        const records = result.records ?? result.lists ?? [];
        setAppendGoods(records);
        setSelectedAppendGoodsId((current) => current || toNumber(records[0]?.id));
        if (records.length === 0) {
          setError("暂无可选加钟项目");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getErrorMessage(err, "加钟项目加载失败，请稍后重试"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingGoods(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingPayWays(true);
    orderApi
      .getPayWays(order.rawId)
      .then((result) => {
        if (cancelled) {
          return;
        }
        const lists = filterAppendPayWays(result.lists ?? []);
        setPayWays(lists);
        setPayMethod(defaultPayWay(lists));
        if (lists.length === 0) {
          setError("暂无可用支付方式");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getErrorMessage(err, "支付方式加载失败，请稍后重试"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingPayWays(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [order.rawId]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedAppendGoodsId) {
      setSettlement(null);
      return;
    }
    setSettling(true);
    setError("");
    orderApi
      .settleOrderAppend(order.rawId, selectedAppendGoodsId, goodsNum)
      .then((result) => {
        if (!cancelled) {
          setSettlement(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSettlement(null);
          setError(getErrorMessage(err, "加钟试算失败，请稍后重试"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSettling(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [goodsNum, order.rawId, selectedAppendGoodsId]);

  const maxSessions = Math.max(1, toNumber(settlement?.max_sessions, 5));
  const canPay =
    Boolean(settlement) &&
    !loadingGoods &&
    !loadingPayWays &&
    !settling &&
    !actionLoading &&
    !error &&
    selectedAppendGoodsId > 0 &&
    payWays.length > 0;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full overflow-y-auto rounded-t-3xl bg-white px-5 pt-5"
        style={{
          maxWidth: 480,
          maxHeight: "calc(100dvh - 16px)",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 32px)",
          animation: "slideUp 0.25s ease",
          WebkitOverflowScrolling: "touch",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex min-w-0 items-center justify-between gap-3">
          <span className="text-base font-bold" style={{ color: "#1A1A1A" }}>我要加钟</span>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center cursor-pointer">
            <i className="ri-close-line text-xl" style={{ color: "#999" }} />
          </button>
        </div>

        <div className="mb-4 flex min-w-0 items-center gap-3 rounded-2xl p-3" style={{ background: "#F8F8F8" }}>
          <img src={order.image} alt={order.service} className="h-14 w-14 flex-shrink-0 rounded-xl object-cover object-top" />
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 text-sm font-bold" style={{ color: "#1A1A1A" }}>{order.service}</p>
            <p className="mt-1 text-xs" style={{ color: "#888" }}>已加钟 {order.appendDuration} 分钟</p>
          </div>
        </div>

        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold" style={{ color: "#333" }}>选择加钟项目</span>
            {loadingGoods ? <span className="text-xs" style={{ color: "#999" }}>加载中...</span> : null}
          </div>
          <div className="max-h-52 space-y-2 overflow-y-auto pr-1">
            {appendGoods.map((item) => {
              const active = selectedAppendGoodsId === toNumber(item.id);
              const fallbackImage = order.image || DEFAULT_ORDER_IMAGE;
              const image = resolveCustomerAssetUrl(String(item.image ?? (Array.isArray(item.goods_image) ? item.goods_image[0] : item.goods_image) ?? "")) || fallbackImage;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setSelectedAppendGoodsId(toNumber(item.id));
                    setGoodsNum(1);
                  }}
                  className="flex w-full items-center gap-3 rounded-2xl p-3 text-left"
                  style={{
                    border: active ? "1.5px solid #FF7B22" : "1px solid #EEEEEE",
                    background: active ? "#FFF7F0" : "#fff",
                  }}
                >
                  <img src={image || order.image} alt={item.name ?? "加钟项目"} className="h-12 w-12 flex-shrink-0 rounded-xl object-cover object-top" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold" style={{ color: "#1A1A1A" }}>{item.name ?? "加钟项目"}</p>
                    <p className="mt-1 text-xs" style={{ color: "#888" }}>{toNumber(item.duration)} 分钟</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold" style={{ color: "#FF5C00" }}>¥ {toNumber(item.price).toFixed(2)}</p>
                    <span
                      className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full"
                      style={{
                        border: active ? "none" : "1px solid #DDDDDD",
                        background: active ? "#FF5C00" : "transparent",
                      }}
                    >
                      {active ? <i className="ri-check-line text-xs text-white" /> : null}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm font-semibold" style={{ color: "#333" }}>加钟次数</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setGoodsNum((value) => Math.max(1, value - 1))}
              className="flex h-8 w-8 items-center justify-center rounded-full"
              style={{ border: "1px solid #E5E5E5", color: "#666" }}
            >
              <i className="ri-subtract-line" />
            </button>
            <span className="w-8 text-center text-sm font-bold">{goodsNum}</span>
            <button
              type="button"
              onClick={() => setGoodsNum((value) => Math.min(maxSessions, value + 1))}
              className="flex h-8 w-8 items-center justify-center rounded-full"
              style={{ border: "1px solid #E5E5E5", color: "#666" }}
            >
              <i className="ri-add-line" />
            </button>
          </div>
        </div>

        <div className="mb-4 rounded-2xl px-4 py-3" style={{ background: "#FFF7F0", border: "1px solid #FFE0CC" }}>
          {settling ? (
            <p className="text-sm" style={{ color: "#FF7B22" }}>试算中...</p>
          ) : error ? (
            <p className="text-sm" style={{ color: "#D94A00" }}>{error}</p>
          ) : settlement ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span style={{ color: "#888" }}>单次时长</span><span>{settlement.duration} 分钟</span></div>
              <div className="flex justify-between"><span style={{ color: "#888" }}>本次加时</span><span>{settlement.total_duration} 分钟</span></div>
              <div className="flex justify-between"><span style={{ color: "#888" }}>预计结束</span><span>{settlement.estimated_finish_time || "-"}</span></div>
              <div className="flex justify-between text-base font-bold"><span>合计</span><span style={{ color: "#FF5C00" }}>¥ {toNumber(settlement.order_amount).toFixed(2)}</span></div>
            </div>
          ) : null}
        </div>

        <div className="mb-5">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold" style={{ color: "#333" }}>支付方式</span>
            {loadingPayWays ? <span className="text-xs" style={{ color: "#999" }}>加载中...</span> : null}
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {payWays.map((item) => {
              const active = normalizePayWay(payMethod) === item.pay_way;
              return (
                <button
                  key={item.id || item.pay_way}
                  onClick={() => setPayMethod(item.pay_way)}
                  className="flex min-w-[8.5rem] flex-shrink-0 items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold"
                  style={{
                    border: active ? "1.5px solid #FF7B22" : "1px solid #E5E5E5",
                    color: active ? "#FF5C00" : "#666",
                    background: active ? "#FFF7F0" : "#fff",
                  }}
                >
                  <i className={`${payWayIcon(item.pay_way)} text-base`} style={{ color: active ? "#FF5C00" : payWayColor(item.pay_way) }} />
                  <span className="truncate">{item.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        <button
          onClick={() => void onPay(order, selectedAppendGoodsId, goodsNum, payMethod)}
          disabled={!canPay}
          className="w-full rounded-full py-3.5 text-base font-bold text-white"
          style={{
            background: canPay ? "linear-gradient(90deg, #FF8C3A, #FF5C00)" : "#E0E0E0",
            color: canPay ? "#fff" : "#BBBBBB",
          }}
        >
          {actionLoading ? "支付中..." : "立即支付"}
        </button>
      </div>
    </div>
  );
}

function FinishEarlyConfirmModal({
  actionLoading,
  onClose,
  onConfirm,
}: {
  actionLoading: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-6"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-2xl bg-white px-5 pb-5 pt-5"
        style={{ maxWidth: 360 }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: "#FFF4EC" }}>
            <i className="ri-error-warning-line text-xl" style={{ color: "#FF5C00" }} />
          </span>
          <span className="text-base font-bold" style={{ color: "#1A1A1A" }}>提前结束订单</span>
        </div>
        <p className="mb-5 text-sm leading-6" style={{ color: "#666" }}>
          当前服务时长未完成，是否确认提前结束订单？
        </p>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={actionLoading}
            className="rounded-full py-3 text-sm font-semibold"
            style={{ border: "1px solid #E5E5E5", color: "#666", background: "#fff" }}
          >
            继续服务
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={actionLoading}
            className="rounded-full py-3 text-sm font-bold text-white"
            style={{
              background: actionLoading ? "#E0E0E0" : "linear-gradient(90deg, #FF8C3A, #FF5C00)",
              color: actionLoading ? "#BBBBBB" : "#fff",
            }}
          >
            {actionLoading ? "处理中..." : "确认结束"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CancelOrderConfirmModal({
  order,
  actionLoading,
  onClose,
  onConfirm,
}: {
  order: Order;
  actionLoading: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const fee = cancelTravelFee(order);
  const hasTravelFee = fee > 0;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-6"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-2xl bg-white px-5 pb-5 pt-5"
        style={{ maxWidth: 360 }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: "#FFF4EC" }}>
            <i className="ri-error-warning-line text-xl" style={{ color: "#FF5C00" }} />
          </span>
          <span className="text-base font-bold" style={{ color: "#1A1A1A" }}>取消订单</span>
        </div>
        <p className="mb-5 text-sm leading-6" style={{ color: "#666" }}>
          {hasTravelFee
            ? `技师已出发，当前订单取消将扣除往返车费 ¥${fee.toFixed(2)}，其余已支付金额将按原路退回。确认取消吗？`
            : "确认取消订单吗？已支付金额将按规则退回。"}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={actionLoading}
            className="rounded-full py-3 text-sm font-semibold"
            style={{ border: "1px solid #E5E5E5", color: "#666", background: "#fff" }}
          >
            再想想
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={actionLoading}
            className="rounded-full py-3 text-sm font-bold text-white"
            style={{
              background: actionLoading ? "#E0E0E0" : "linear-gradient(90deg, #FF8C3A, #FF5C00)",
              color: actionLoading ? "#BBBBBB" : "#fff",
            }}
          >
            {actionLoading ? "处理中..." : "确认取消"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteOrderConfirmModal({
  order,
  actionLoading,
  onClose,
  onConfirm,
}: {
  order: Order;
  actionLoading: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-6"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-2xl bg-white px-5 pb-5 pt-5"
        style={{ maxWidth: 360 }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: "#F5F5F7" }}>
            <i className="ri-delete-bin-line text-xl" style={{ color: "#777" }} />
          </span>
          <span className="text-base font-bold" style={{ color: "#1A1A1A" }}>删除订单</span>
        </div>
        <p className="mb-5 text-sm leading-6" style={{ color: "#666" }}>
          删除后该订单将不再在用户端展示，确认删除“{order.service}”吗？
        </p>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={actionLoading}
            className="rounded-full py-3 text-sm font-semibold"
            style={{ border: "1px solid #E5E5E5", color: "#666", background: "#fff" }}
          >
            取消
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={actionLoading}
            className="rounded-full py-3 text-sm font-bold text-white"
            style={{
              background: actionLoading ? "#E0E0E0" : "#666",
              color: actionLoading ? "#BBBBBB" : "#fff",
            }}
          >
            {actionLoading ? "处理中..." : "确认删除"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderDetailPage({
  order,
  actionError,
  actionLoading,
  payFeedback,
  onBack,
  onCancelOrder,
  onDeleteOrder,
  onPayOrder,
  onPayAppend,
  onPayPendingAppend,
  onFinishEarly,
  onOpenReview,
  onClosePayFeedback,
}: {
  order: Order;
  actionError: string;
  actionLoading: boolean;
  payFeedback: PayFeedback;
  onBack: () => void;
  onCancelOrder: (order: Order) => Promise<void>;
  onDeleteOrder: (order: Order) => Promise<void>;
  onPayOrder: (order: Order, method: PayMethod) => Promise<void>;
  onPayAppend: (order: Order, appendGoodsId: number, goodsNum: number, method: PayMethod) => Promise<void>;
  onPayPendingAppend: (order: Order, appendId: number, method: PayMethod) => Promise<void>;
  onFinishEarly: (order: Order) => Promise<void>;
  onOpenReview: (order: Order) => void;
  onClosePayFeedback: () => void;
}) {
  const navigate = useNavigate();
  const [showCancelReason, setShowCancelReason] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showPendingAppendPayModal, setShowPendingAppendPayModal] = useState(false);
  const [showAppendModal, setShowAppendModal] = useState(false);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [preferredPayMethod, setPreferredPayMethod] = useState<PayMethod | null>(null);

  const isPendingPay = order.status === "pending_pay";
  const isOnTheWay = order.status === "on_the_way";
  const isArrived = order.status === "arrived";
  const isInProgress = order.status === "in_progress";
  const isPendingReview = order.status === "pending_review";
  const isCompleted = order.status === "completed";
  const isCancelled = order.status === "cancelled";
  const canCancelActiveOrder = order.canCancel && [1, 2, 3, 4].includes(order.rawStatus);
  const canDelete = canDeleteOrder(order);
  const pendingAppend = order.pendingAppendRecords[0];

  return (
    <div
      className="customer-fixed-shell fixed inset-y-0 z-50 flex flex-col"
      style={{
        background: "#F4F4F6",
      }}
    >
      <div
        style={{
          background: isCancelled
            ? "linear-gradient(135deg, #AAAAAA 0%, #888888 100%)"
            : isCompleted
              ? "linear-gradient(135deg, #17B978 0%, #0FA066 100%)"
              : isPendingReview
                ? "linear-gradient(135deg, #F0A500 0%, #D08800 100%)"
                : "linear-gradient(135deg, #FF8C3A 0%, #FF5C00 100%)",
        }}
      >
        <div className="flex min-w-0 items-center gap-3 px-4 pb-3 pt-4">
          <button onClick={onBack} className="flex h-8 w-8 flex-shrink-0 items-center justify-center cursor-pointer">
            <i className="ri-arrow-left-line text-xl text-white" />
          </button>
          <span className="min-w-0 flex-1 truncate text-base font-bold text-white">订单详情</span>
          <span className="max-w-[42%] truncate text-right text-xs text-white/60">{order.orderNo}</span>
        </div>

        <div className="px-4 pb-5 pt-1">
          {isPendingPay ? (
            <div>
              <div className="mb-1.5 flex min-w-0 items-center gap-2">
                <i className="ri-time-line text-lg text-white" />
                <span className="text-lg font-bold text-white">待支付</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-white/80">请在</span>
                {order.countdown !== undefined ? <CountdownText initialSeconds={order.countdown} /> : null}
                <span className="text-sm text-white/80">内完成支付，否则订单将自动取消</span>
              </div>
            </div>
          ) : null}

          {isOnTheWay ? (
            <div>
              <div className="mb-2 flex min-w-0 items-center gap-2">
                <i className="ri-motorbike-line text-xl text-white" />
                <span className="text-lg font-bold text-white">{order.statusLabel || "进行中"}</span>
              </div>
              <p className="text-sm text-white/80">
                {order.arrivalMinutes ? `预计 ${order.arrivalMinutes} 分钟后到达` : order.subStatus}
              </p>
            </div>
          ) : null}

          {isArrived ? (
            <div>
              <div className="mb-2 flex min-w-0 items-center gap-2">
                <i className="ri-map-pin-2-line text-xl text-white" />
                <span className="text-lg font-bold text-white">{order.statusLabel || "已到达"}</span>
              </div>
              <p className="text-sm text-white/80">{order.subStatus || "技师已到达"}</p>
            </div>
          ) : null}

          {isInProgress ? (
            <div>
              <div className="mb-2 flex min-w-0 items-center gap-2">
                <i className="ri-service-line text-xl text-white" />
                <span className="text-lg font-bold text-white">{order.statusLabel || "服务中"}</span>
              </div>
              <p className="text-sm text-white/80">{order.subStatus || "服务正在进行中"}</p>
            </div>
          ) : null}

          {isPendingReview ? (
            <div>
              <div className="mb-2 flex min-w-0 items-center gap-2">
                <i className="ri-star-line text-xl text-white" />
                <span className="text-lg font-bold text-white">服务已完成</span>
              </div>
              <p className="text-sm text-white/80">本订单待评价，欢迎留下真实体验反馈</p>
            </div>
          ) : null}

          {isCompleted ? (
            <div>
              <div className="mb-2 flex min-w-0 items-center gap-2">
                <i className="ri-checkbox-circle-line text-xl text-white" />
                <span className="text-lg font-bold text-white">服务已完成</span>
              </div>
              <p className="text-sm text-white/80">感谢您的使用，期待再次为您服务</p>
            </div>
          ) : null}

          {isCancelled ? (
            <div>
              <div className="mb-2 flex min-w-0 items-center gap-2">
                <i className="ri-close-circle-line text-xl text-white" />
                <span className="text-lg font-bold text-white">订单已取消</span>
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <span className="min-w-0 flex-1 break-words text-sm text-white/80">取消原因：{order.cancelReason}</span>
                <button
                  onClick={() => setShowCancelReason(true)}
                  className="flex-shrink-0 cursor-pointer text-xs underline underline-offset-2"
                  style={{ color: "rgba(255,255,255,0.9)" }}
                >
                  查看详情
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 112px)" }}
      >
        {actionError ? (
          <div className="mx-3 mt-3 rounded-2xl px-4 py-3 text-sm" style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}>
            {actionError}
          </div>
        ) : null}

        {!isPendingPay && !isCancelled ? (
          <div className="mx-3 mt-3 rounded-2xl bg-white px-4 py-4" style={{ border: "1px solid #F0F0F0" }}>
            <div className="flex min-w-0 items-center gap-3">
              <div className="relative h-14 w-14 overflow-hidden rounded-2xl flex-shrink-0">
                <img src={order.therapistAvatar} alt={order.therapist} className="h-full w-full object-cover object-top" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex min-w-0 items-center gap-2">
                  <span className="min-w-0 truncate text-sm font-bold" style={{ color: "#1A1A1A" }}>
                    {order.therapist}
                  </span>
                  <span
                    className="flex-shrink-0 rounded-full px-1.5 py-0.5 text-xs font-semibold"
                    style={{ background: "#FFF3EC", color: "#FF7B22" }}
                  >
                    认证技师
                  </span>
                </div>
                {order.therapistPhoneMasked ? (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <i className="ri-phone-line text-xs" style={{ color: "#BBBBBB" }} />
                    <span className="text-xs" style={{ color: "#999" }}>
                      {order.therapistPhoneMasked}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        <div className="mx-3 mt-2.5 rounded-2xl bg-white px-4 py-4" style={{ border: "1px solid #F0F0F0" }}>
          <div className="flex min-w-0 items-start gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0"
              style={{ background: "#FFF3EC" }}
            >
              <i className="ri-map-pin-2-fill text-lg" style={{ color: "#FF7B22" }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="mb-0.5 text-sm font-semibold" style={{ color: "#1A1A1A" }}>
                上门地址
              </p>
              <p className="break-words text-xs leading-relaxed" style={{ color: "#AAAAAA" }}>
                {order.address}
              </p>
            </div>
          </div>
        </div>

        <div className="mx-3 mt-2.5 rounded-2xl bg-white px-4 py-1" style={{ border: "1px solid #F0F0F0" }}>
          <div className="flex min-w-0 items-center gap-3 py-3.5" style={{ borderBottom: "1px solid #F8F8F8" }}>
            <div className="h-14 w-14 overflow-hidden rounded-xl flex-shrink-0">
              <img src={order.image} alt={order.service} className="h-full w-full object-cover object-top" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-sm font-semibold leading-snug" style={{ color: "#1A1A1A" }}>
                {order.service}
              </p>
              <span
                className="mt-1 inline-block rounded-full px-2 py-0.5 text-xs"
                style={{ background: "#F4F4F6", color: "#888" }}
              >
                {order.duration}
              </span>
            </div>
            <span className="text-xs flex-shrink-0" style={{ color: "#AAAAAA" }}>
              x{order.qty}
            </span>
          </div>

          {[
            { label: "服务技师", value: order.therapist },
            { label: "服务时间", value: order.serviceTime },
            ...(order.note ? [{ label: "备注", value: order.note }] : []),
          ].map((row) => (
            <div key={row.label} className="flex min-w-0 items-start justify-between gap-3 py-3" style={{ borderBottom: "1px solid #F8F8F8" }}>
              <span className="mr-4 min-w-[72px] flex-shrink-0 text-xs" style={{ color: "#AAAAAA" }}>
                {row.label}
              </span>
              <span className="min-w-0 flex-1 break-words text-right text-xs font-medium" style={{ color: "#333" }}>
                {row.value}
              </span>
            </div>
          ))}
        </div>

        {order.pendingAppendCount > 0 ? (
          <div className="mx-3 mt-2.5 rounded-2xl bg-white px-4 py-4" style={{ border: "1px solid #FFE0CC" }}>
            <div className="mb-3 flex min-w-0 items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <i className="ri-timer-flash-line text-base" style={{ color: "#FF7B22" }} />
                <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>加钟待支付</span>
              </div>
              <span className="rounded-full px-2 py-0.5 text-xs font-semibold" style={{ background: "#FFF7F0", color: "#FF5C00" }}>
                {order.pendingAppendCount}单
              </span>
            </div>
            <div className="space-y-2">
              {order.pendingAppendRecords.map((record) => (
                <div key={record.id || record.sn} className="flex min-w-0 items-center justify-between gap-3 rounded-xl px-3 py-2" style={{ background: "#FFF7F0" }}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold" style={{ color: "#333" }}>{record.goods_name || "加钟项目"}</p>
                    <p className="mt-0.5 text-xs" style={{ color: "#999" }}>{appendRecordDuration(record)}分钟 / 等待支付</p>
                  </div>
                  <span className="text-xs font-bold" style={{ color: "#FF5C00" }}>¥ {toNumber(record.order_amount).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {order.appendCount > 0 ? (
          <div className="mx-3 mt-2.5 rounded-2xl bg-white px-4 py-4" style={{ border: "1px solid #F0F0F0" }}>
            <div className="mb-3 flex min-w-0 items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <i className="ri-time-line text-base" style={{ color: "#FF7B22" }} />
                <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>加钟记录</span>
              </div>
              <span className="text-xs font-semibold" style={{ color: "#FF5C00" }}>
                {order.appendCount}次 / {order.appendDuration}分钟
              </span>
            </div>
            <div className="mb-3 grid grid-cols-2 gap-2">
              <div className="rounded-xl px-3 py-2" style={{ background: "#FFF7F0" }}>
                <p className="text-xs" style={{ color: "#999" }}>加钟金额</p>
                <p className="mt-0.5 text-sm font-bold" style={{ color: "#FF5C00" }}>¥ {order.appendAmount.toFixed(2)}</p>
              </div>
              <div className="rounded-xl px-3 py-2" style={{ background: "#F8F8F8" }}>
                <p className="text-xs" style={{ color: "#999" }}>预计结束</p>
                <p className="mt-0.5 truncate text-sm font-bold" style={{ color: "#333" }}>{order.appendEstimatedFinishTime || "-"}</p>
              </div>
            </div>
            <div className="space-y-2">
              {order.appendRecords.map((record) => (
                <div key={record.id || record.sn} className="flex min-w-0 items-center justify-between gap-3 rounded-xl px-3 py-2" style={{ background: "#FAFAFA" }}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold" style={{ color: "#333" }}>{record.sn}</p>
                    <p className="mt-0.5 text-xs" style={{ color: "#999" }}>{appendRecordDuration(record)}分钟 / {record.pay_time || "-"}</p>
                  </div>
                  <span className="text-xs font-bold" style={{ color: "#FF5C00" }}>¥ {toNumber(record.order_amount).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {!isCancelled ? (
          <div className="mx-3 mt-2.5 rounded-2xl bg-white px-4 py-1" style={{ border: "1px solid #F0F0F0" }}>
            {[
              { label: "服务项目", value: `¥ ${order.originalPrice.toFixed(2)}`, color: "#333" },
              { label: "优惠抵扣", value: `-¥ ${order.coupon.toFixed(2)}`, color: "#FF7B22" },
              { label: "出行费用", value: `¥ ${order.travel.toFixed(2)} / ${order.travelKm}`, color: "#333" },
              ...(order.appendAmount > 0 ? [{ label: "加钟费用", value: `¥ ${order.appendAmount.toFixed(2)}`, color: "#FF5C00" }] : []),
              { label: "支付方式", value: order.payMethod, color: "#333" },
            ].map((row) => (
              <div key={row.label} className="flex min-w-0 items-center justify-between gap-3 py-3" style={{ borderBottom: "1px solid #F8F8F8" }}>
                <span className="text-xs" style={{ color: "#AAAAAA" }}>
                  {row.label}
                </span>
                <span className="min-w-0 flex-1 break-words text-right text-xs font-medium" style={{ color: row.color }}>
                  {row.value}
                </span>
              </div>
            ))}
            <div className="flex min-w-0 items-center justify-between gap-3 py-3">
              <span className="text-sm font-semibold" style={{ color: "#1A1A1A" }}>
                实付金额
              </span>
              <span className="min-w-0 text-right text-base font-bold" style={{ color: "#FF5C00" }}>
                ¥ {order.total.toFixed(2)}
              </span>
            </div>
          </div>
        ) : null}

        <div className="mx-3 mt-2.5 rounded-2xl bg-white px-4 py-1" style={{ border: "1px solid #F0F0F0" }}>
          {[
            { label: "订单编号", value: order.orderNo },
            { label: "下单时间", value: order.createTime || "-" },
            { label: "支付时间", value: order.payTime },
            ...(isCancelled && order.cancelTime ? [{ label: "取消时间", value: order.cancelTime }] : []),
          ].map((row, index, rows) => (
            <div
              key={row.label}
              className="flex min-w-0 items-center justify-between gap-3 py-3"
              style={{ borderBottom: index < rows.length - 1 ? "1px solid #F8F8F8" : "none" }}
            >
              <span className="text-xs" style={{ color: "#AAAAAA" }}>
                {row.label}
              </span>
              <span className="min-w-0 flex-1 break-all text-right text-xs font-medium" style={{ color: "#666" }}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div
        className="customer-fixed-shell fixed bottom-0 z-50 w-full bg-white px-4 pt-3"
        style={{
          borderTop: "1px solid #F0F0F0",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
        }}
      >
        {isPendingPay ? (
          <div className="flex min-w-0 gap-3">
            <button
              onClick={() => setShowCancelConfirm(true)}
              disabled={actionLoading || !order.canCancel}
              className="min-w-0 flex-1 rounded-full py-3.5 text-sm font-semibold cursor-pointer whitespace-nowrap"
              style={{
                border: "1.5px solid #DDDDDD",
                color: "#888",
                background: "#fff",
                opacity: actionLoading || !order.canCancel ? 0.6 : 1,
              }}
            >
              {actionLoading ? "处理中..." : "取消订单"}
            </button>
            <button
              onClick={() => setShowPayModal(true)}
              disabled={actionLoading || !order.canPay}
              className="min-w-0 flex-1 rounded-full py-3.5 text-sm font-bold text-white cursor-pointer whitespace-nowrap"
              style={{
                background:
                  actionLoading || !order.canPay
                    ? "#E0E0E0"
                    : "linear-gradient(90deg, #FF8C3A 0%, #FF5C00 100%)",
                color: actionLoading || !order.canPay ? "#BBBBBB" : "#fff",
              }}
            >
              去支付
            </button>
          </div>
        ) : canCancelActiveOrder ? (
          <button
            onClick={() => setShowCancelConfirm(true)}
            disabled={actionLoading}
            className="w-full rounded-full py-3.5 text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{
              border: "1.5px solid #DDDDDD",
              color: actionLoading ? "#BBBBBB" : "#888",
              background: "#fff",
              opacity: actionLoading ? 0.7 : 1,
            }}
          >
            {actionLoading ? "处理中..." : "取消订单"}
          </button>
        ) : isCancelled ? (
          <button
            onClick={() => navigate("/")}
            className="w-full rounded-full py-3.5 text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ border: "1.5px solid #FF7B22", color: "#FF7B22", background: "#fff" }}
          >
            返回首页
          </button>
        ) : isInProgress ? (
          <>
            {order.canPayPendingAppend && pendingAppend ? (
              <button
                onClick={() => setShowPendingAppendPayModal(true)}
                disabled={actionLoading}
                className="w-full rounded-full py-3.5 text-sm font-bold text-white cursor-pointer whitespace-nowrap"
                style={{
                  background: actionLoading ? "#E0E0E0" : "linear-gradient(90deg, #FF8C3A 0%, #FF5C00 100%)",
                  color: actionLoading ? "#BBBBBB" : "#fff",
                }}
              >
                支付加钟 ¥ {toNumber(pendingAppend.order_amount).toFixed(2)}
              </button>
            ) : null}
            {order.canAppend ? (
              <button
                onClick={() => setShowAppendModal(true)}
                disabled={actionLoading}
                className={`${order.canPayPendingAppend ? "mt-2" : ""} w-full rounded-full py-3.5 text-sm font-bold text-white cursor-pointer whitespace-nowrap`}
                style={{
                  background: actionLoading ? "#E0E0E0" : "linear-gradient(90deg, #FF8C3A 0%, #FF5C00 100%)",
                  color: actionLoading ? "#BBBBBB" : "#fff",
                }}
              >
                我要加钟
              </button>
            ) : null}
            {order.canFinishEarly ? (
              <button
                onClick={() => setShowFinishConfirm(true)}
                disabled={actionLoading}
                className="mt-2 w-full rounded-full py-3.5 text-sm font-semibold cursor-pointer whitespace-nowrap"
                style={{
                  border: "1.5px solid #FF7B22",
                  color: actionLoading ? "#BBBBBB" : "#FF5C00",
                  background: "#fff",
                  opacity: actionLoading ? 0.7 : 1,
                }}
              >
                提前结束
              </button>
            ) : null}
          </>
        ) : isPendingReview ? (
          <button
            onClick={() => onOpenReview(order)}
            disabled={!order.canComment}
            className="w-full rounded-full py-3.5 text-sm font-bold text-white cursor-pointer whitespace-nowrap"
            style={{
              background: order.canComment ? "linear-gradient(90deg, #FFC840, #F0A500)" : "#E0E0E0",
              color: order.canComment ? "#fff" : "#BBBBBB",
            }}
          >
            去评价
          </button>
        ) : (
          <button
            onClick={() => navigate("/")}
            className="w-full rounded-full py-3.5 text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ border: "1.5px solid #FF7B22", color: "#FF7B22", background: "#fff" }}
          >
            返回首页
          </button>
        )}
        {canDelete ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={actionLoading}
            className="mt-2 w-full rounded-full py-3 text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{
              border: "1.5px solid #E5E5E5",
              color: actionLoading ? "#BBBBBB" : "#777",
              background: "#fff",
              opacity: actionLoading ? 0.7 : 1,
            }}
          >
            删除订单
          </button>
        ) : null}
      </div>

      {showCancelReason && order.cancelReason ? (
        <CancelReasonModal order={order} onClose={() => setShowCancelReason(false)} />
      ) : null}

      {showPayModal ? (
        <PayMethodModal
          order={order}
          paying={actionLoading}
          initialMethod={preferredPayMethod ?? undefined}
          onClose={() => setShowPayModal(false)}
          onPay={(method) => {
            setPreferredPayMethod(method);
            void onPayOrder(order, method);
            setShowPayModal(false);
          }}
        />
      ) : null}

      {showPendingAppendPayModal && pendingAppend ? (
        <PayMethodModal
          order={order}
          paying={actionLoading}
          initialMethod={preferredPayMethod ?? undefined}
          onClose={() => setShowPendingAppendPayModal(false)}
          onPay={(method) => {
            setPreferredPayMethod(method);
            void onPayPendingAppend(order, pendingAppend.id, method);
            setShowPendingAppendPayModal(false);
          }}
        />
      ) : null}

      {payFeedback ? (
        <PayFeedbackModal
          feedback={payFeedback}
          onClose={onClosePayFeedback}
          onSwitchWechat={() => {
            onClosePayFeedback();
            setPreferredPayMethod("wechat");
            setShowPayModal(true);
          }}
        />
      ) : null}

      {showAppendModal ? (
        <AppendOrderModal
          order={order}
          actionLoading={actionLoading}
          onClose={() => setShowAppendModal(false)}
          onPay={async (targetOrder, appendGoodsId, goodsNum, method) => {
            await onPayAppend(targetOrder, appendGoodsId, goodsNum, method);
            setShowAppendModal(false);
          }}
        />
      ) : null}

      {showFinishConfirm ? (
        <FinishEarlyConfirmModal
          actionLoading={actionLoading}
          onClose={() => setShowFinishConfirm(false)}
          onConfirm={async () => {
            await onFinishEarly(order);
            setShowFinishConfirm(false);
          }}
        />
      ) : null}

      {showCancelConfirm ? (
        <CancelOrderConfirmModal
          order={order}
          actionLoading={actionLoading}
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={async () => {
            await onCancelOrder(order);
            setShowCancelConfirm(false);
          }}
        />
      ) : null}

      {showDeleteConfirm ? (
        <DeleteOrderConfirmModal
          order={order}
          actionLoading={actionLoading}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            await onDeleteOrder(order);
            setShowDeleteConfirm(false);
          }}
        />
      ) : null}
    </div>
  );
}

function OrderCard({
  order,
  onDetail,
  onOpenReview,
}: {
  order: Order;
  onDetail: (order: Order) => void;
  onOpenReview: (order: Order) => void;
}) {
  const isPending = order.status === "pending_pay";
  const isOnWay = order.status === "on_the_way";
  const isArrived = order.status === "arrived";
  const isServing = order.status === "in_progress";
  const isPendingReview = order.status === "pending_review";
  const isCancelled = order.status === "cancelled";
  const isActive = isOnWay || isArrived || isServing;
  const themeKey: Exclude<OrderStatus, "all"> =
    order.status === "all" ? "pending_pay" : order.status;
  const theme = STATUS_THEME[themeKey];

  return (
    <div
      className="cursor-pointer overflow-hidden rounded-2xl"
      style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.05)" }}
      onClick={() => onDetail(order)}
    >
      <div className="relative px-4 pb-4 pt-4" style={{ background: theme.gradient }}>
        <div
          className="absolute bottom-2 right-2 flex items-center justify-center"
          style={{ width: 64, height: 64, opacity: 0.13, pointerEvents: "none" }}
        >
          <i className={theme.icon} style={{ fontSize: 56, color: theme.accentColor }} />
        </div>

        <div className="mb-3 flex min-w-0 items-center justify-between gap-3">
          <span className="min-w-0 truncate text-xs" style={{ color: theme.accentColor, opacity: 0.7 }}>
            {order.createTime.slice(0, 10)}
          </span>
          <div
            className="flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1"
            style={{ background: "rgba(255,255,255,0.75)", backdropFilter: "blur(4px)" }}
          >
            {theme.tagDot ? (
              <span
                className="h-1.5 w-1.5 rounded-full flex-shrink-0"
                style={{
                  background: theme.tagDot,
                  animation: isActive ? "pulse-dot 1.5s infinite" : "none",
                }}
              />
            ) : null}
            <span className="text-xs font-bold" style={{ color: theme.tagColor }}>
              {theme.tagLabel}
            </span>
          </div>
        </div>

        {isPending && order.countdown !== undefined ? (
          <div
            className="mb-3 flex w-fit items-center gap-1.5 rounded-xl px-3 py-1.5"
            style={{ background: "rgba(255,255,255,0.6)" }}
          >
            <i className="ri-time-line text-xs" style={{ color: theme.accentColor }} />
            <span className="text-xs" style={{ color: theme.accentColor }}>
              支付剩余
            </span>
            <CountdownText initialSeconds={order.countdown} />
          </div>
        ) : null}

        {isOnWay && order.arrivalMinutes ? (
          <div
            className="mb-3 flex w-fit items-center gap-1.5 rounded-xl px-3 py-1.5"
            style={{ background: "rgba(255,255,255,0.6)" }}
          >
            <i className="ri-motorbike-line text-xs" style={{ color: theme.accentColor }} />
            <span className="text-xs font-semibold" style={{ color: theme.accentColor }}>
              技师约 {order.arrivalMinutes} 分钟后到达
            </span>
          </div>
        ) : null}

        {isArrived ? (
          <div
            className="mb-3 flex w-fit items-center gap-1.5 rounded-xl px-3 py-1.5"
            style={{ background: "rgba(255,255,255,0.6)" }}
          >
            <i className="ri-map-pin-2-line text-xs" style={{ color: theme.accentColor }} />
            <span className="text-xs font-semibold" style={{ color: theme.accentColor }}>
              技师已到达
            </span>
          </div>
        ) : null}

        {isServing ? (
          <div
            className="mb-3 flex w-fit items-center gap-1.5 rounded-xl px-3 py-1.5"
            style={{ background: "rgba(255,255,255,0.6)" }}
          >
            <i className="ri-service-line text-xs" style={{ color: theme.accentColor }} />
            <span className="text-xs font-semibold" style={{ color: theme.accentColor }}>
              服务正在进行中
            </span>
          </div>
        ) : null}

        <p className="line-clamp-2 pr-16 text-sm font-bold leading-snug" style={{ color: "#1A1A1A" }}>
          {order.service}
        </p>

        <div className="mt-2 flex min-w-0 items-center gap-2">
          <div
            className="h-5 w-5 overflow-hidden rounded-full flex-shrink-0"
            style={{ border: `1.5px solid ${theme.accentColor}50` }}
          >
            <img src={order.therapistAvatar} alt={order.therapist} className="h-full w-full object-cover object-top" />
          </div>
          <span className="min-w-0 truncate text-xs font-semibold" style={{ color: theme.accentColor }}>
            {order.therapist}
          </span>
          <span className="flex-shrink-0" style={{ color: "#CCCCCC", fontSize: 10 }}>·</span>
          <span className="flex-shrink-0 text-xs" style={{ color: "#AAAAAA" }}>
            {order.duration}
          </span>
        </div>
        {order.appendCount > 0 ? (
          <div className="mt-2 flex w-fit items-center gap-1.5 rounded-full px-2 py-1" style={{ background: "rgba(255,255,255,0.72)" }}>
            <i className="ri-time-line text-xs" style={{ color: "#FF5C00" }} />
            <span className="text-xs font-semibold" style={{ color: "#FF5C00" }}>
              含加钟 {order.appendCount}次 / {order.appendDuration}分钟
            </span>
          </div>
        ) : null}
      </div>

      <div style={{ height: 1, background: `${theme.glowColor}60` }} />

      <div className="flex min-w-0 items-center justify-between gap-3 px-4 py-3" style={{ background: "#fff" }}>
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="text-xs" style={{ color: "#AAAAAA" }}>
            合计
          </span>
          <span className="min-w-0 truncate text-base font-bold" style={{ color: "#FF5C00" }}>
            ¥{order.total.toFixed(2)}
          </span>
        </div>

        <div className="flex flex-shrink-0 gap-2" onClick={(event) => event.stopPropagation()}>
          <button
            onClick={() => (isPendingReview ? onOpenReview(order) : onDetail(order))}
            className="rounded-full px-4 py-1.5 text-xs font-bold cursor-pointer whitespace-nowrap"
            style={
              isPending
                ? { background: "linear-gradient(90deg, #FF8C3A, #FF5C00)", color: "#fff" }
                : isPendingReview
                  ? { background: "linear-gradient(90deg, #FFC840, #F0A500)", color: "#fff" }
                : { border: "1.5px solid #FF7B22", color: "#FF7B22", background: "#fff" }
            }
          >
            {isPending ? "去支付" : isPendingReview ? "去评价" : "查看详情"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<OrderStatus | "all">("all");
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);
  const [reviewOrder, setReviewOrder] = useState<Order | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [payFeedback, setPayFeedback] = useState<PayFeedback>(null);
  const detailOrderRef = useRef<Order | null>(null);

  const redirectToLogin = () => {
    navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
  };

  const loadOrders = async () => {
    if (!auth.getToken()) {
      redirectToLogin();
      return;
    }

    try {
      const result = await orderApi.getOrderList({ page_no: 1, page_size: 100 });
      const mappedOrders = Array.isArray(result.lists) ? result.lists.map(mapApiOrderToView) : [];
      setOrders(mappedOrders);
      setPageError("");
      setDetailOrder((current) => {
        if (!current) {
          return null;
        }
        return mappedOrders.find((item) => item.rawId === current.rawId) ?? current;
      });
      setReviewOrder((current) => {
        if (!current) {
          return null;
        }
        return mappedOrders.find((item) => item.rawId === current.rawId) ?? current;
      });
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setPageError(getErrorMessage(error, "订单数据加载失败，请稍后重试"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOrders();
  }, []);

  useEffect(() => {
    detailOrderRef.current = detailOrder;
  }, [detailOrder]);

  useEffect(() => {
    const handleOrderNotify = (event: Event) => {
      const message = (event as CustomEvent<AppNotifyMessage>).detail;
      void (async () => {
        await loadOrders();
        const currentDetail = detailOrderRef.current;
        const notifiedOrderId = Number(message?.orderId || 0);
        if (!currentDetail || (notifiedOrderId > 0 && currentDetail.rawId !== notifiedOrderId)) {
          return;
        }
        try {
          const detail = await orderApi.getOrderDetail(currentDetail.rawId);
          setDetailOrder(mapApiOrderToView(detail as OrderListItem));
        } catch (error) {
          if (error instanceof RequestError && error.code === 1001) {
            redirectToLogin();
          }
        }
      })();
    };

    window.addEventListener(CUSTOMER_ORDER_NOTIFY_EVENT, handleOrderNotify);
    return () => window.removeEventListener(CUSTOMER_ORDER_NOTIFY_EVENT, handleOrderNotify);
  }, []);

  const filteredOrders = useMemo(
    () => orders.filter((item) => matchesTab(item, activeTab)),
    [activeTab, orders],
  );

  const activeCount = useMemo(
    () =>
      orders.filter(
        (item) => item.status === "on_the_way" || item.status === "arrived" || item.status === "in_progress",
      ).length,
    [orders],
  );

  const openOrderDetail = async (order: Order) => {
    setDetailOrder(order);
    setActionError("");
    setPayFeedback(null);

    try {
      const detail = await orderApi.getOrderDetail(order.rawId);
      setDetailOrder(mapApiOrderToView(detail as OrderListItem));
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setActionError(getErrorMessage(error, "订单详情加载失败，已展示列表快照数据"));
    }
  };

  const handleOpenReview = (order: Order) => {
    if (!order.canComment || order.orderGoodsId <= 0) {
      return;
    }
    setReviewOrder(order);
  };

  const handleReviewSuccess = async () => {
    await loadOrders();
  };

  const handleCancelOrder = async (order: Order) => {
    setActionLoading(true);
    setActionError("");
    setPayFeedback(null);

    try {
      await orderApi.cancelOrder(order.rawId);
      await loadOrders();
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setActionError(getErrorMessage(error, "订单取消失败，请稍后重试"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteOrder = async (order: Order) => {
    if (!canDeleteOrder(order)) {
      setActionError("服务中的订单无法删除");
      return;
    }
    setActionLoading(true);
    setActionError("");
    setPayFeedback(null);

    try {
      await orderApi.deleteOrder(order.rawId);
      setDetailOrder(null);
      setOrders((current) => current.filter((item) => item.rawId !== order.rawId));
      await loadOrders();
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setActionError(getErrorMessage(error, "订单删除失败，请稍后重试"));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePayOrder = async (order: Order, method: PayMethod) => {
    setActionLoading(true);
    setActionError("");
    setPayFeedback(null);

    try {
      const payWay = normalizePayWay(method);
      const prepayResult = await orderApi.prepayOrder(order.rawId, payWay);
      if (payWay === 1) {
        const mockModeEnabled = Boolean(prepayResult.config?.mock_mode);
        if (isWechatPayAlreadyPaid(prepayResult.config)) {
          await waitForWechatPayResultReady(() => orderApi.getPayResult(order.rawId));
        } else if (!mockModeEnabled && isWechatJsapiPayConfig(prepayResult.config)) {
          await invokeWechatJsapiPay(prepayResult.config);
          await waitForWechatPayResultReady(() => orderApi.getPayResult(order.rawId));
        } else {
        if (!mockModeEnabled) {
          throw new Error("当前环境未启用模拟微信支付");
        }
        const notifyResult = await orderApi.mockPayNotify(order.rawId, "success", payWay);
        if (toNumber(notifyResult.pay_status) !== 1) {
          throw new Error(notifyResult.message || "模拟微信支付未完成");
        }
        }
      }

      const payResult = await orderApi.getPayResult(order.rawId);
      if (toNumber(payResult.pay_status) !== 1) {
        throw new Error("支付结果未确认，请稍后刷新重试");
      }

      await loadOrders();
      const detail = await orderApi.getOrderDetail(order.rawId);
      setDetailOrder(mapApiOrderToView(detail as OrderListItem));
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      const message = getErrorMessage(error, "订单支付失败，请稍后重试");
      if (normalizePayWay(method) === 3 && isBalanceInsufficientMessage(message)) {
        setPayFeedback({
          type: "balance",
          title: "余额不足",
          message: "当前账户余额不足以支付本单，请更换微信支付，或购卡后再继续下单。",
        });
      } else {
        setPayFeedback({
          type: "payment",
          title: "支付未完成",
          message,
        });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleFinishEarly = async (order: Order) => {
    setActionLoading(true);
    setActionError("");
    setPayFeedback(null);

    try {
      await orderApi.finishOrder(order.rawId);
      await loadOrders();
      const detail = await orderApi.getOrderDetail(order.rawId);
      setDetailOrder(mapApiOrderToView(detail as OrderListItem));
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setActionError(getErrorMessage(error, "提前结束订单失败，请稍后重试"));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePayAppend = async (order: Order, appendGoodsId: number, goodsNum: number, method: PayMethod) => {
    setActionLoading(true);
    setActionError("");
    setPayFeedback(null);

    try {
      if (!appendGoodsId) {
        throw new Error("加钟项目缺失");
      }
      const payWay = normalizePayWay(method);
      const appendOrder = await orderApi.submitOrderAppend(order.rawId, appendGoodsId, goodsNum);
      const prepayResult = await orderApi.prepayOrderAppend(appendOrder.id, payWay);
      if (payWay === 1) {
        const mockModeEnabled = Boolean(prepayResult.config?.mock_mode);
        if (isWechatPayAlreadyPaid(prepayResult.config)) {
          await waitForWechatPayResultReady(() => orderApi.getPayResult(appendOrder.id, "orderAppend"));
        } else if (!mockModeEnabled && isWechatJsapiPayConfig(prepayResult.config)) {
          await invokeWechatJsapiPay(prepayResult.config);
          await waitForWechatPayResultReady(() => orderApi.getPayResult(appendOrder.id, "orderAppend"));
        } else {
        if (!mockModeEnabled) {
          throw new Error("当前环境未启用模拟微信支付");
        }
        const notifyResult = await orderApi.mockPayNotify(appendOrder.id, "success", payWay, "orderAppend");
        if (toNumber(notifyResult.pay_status) !== 1) {
          throw new Error(notifyResult.message || "模拟微信支付未完成");
        }
        }
      }

      const payResult = await orderApi.getPayResult(appendOrder.id, "orderAppend");
      if (toNumber(payResult.pay_status) !== 1) {
        throw new Error("加钟支付结果未确认，请稍后刷新重试");
      }

      await loadOrders();
      const detail = await orderApi.getOrderDetail(order.rawId);
      setDetailOrder(mapApiOrderToView(detail as OrderListItem));
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setActionError(getErrorMessage(error, "加钟支付失败，请稍后重试"));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePayPendingAppend = async (order: Order, appendId: number, method: PayMethod) => {
    setActionLoading(true);
    setActionError("");
    setPayFeedback(null);

    try {
      if (!appendId) {
        throw new Error("加钟待支付单缺失");
      }
      const payWay = normalizePayWay(method);
      const prepayResult = await orderApi.prepayOrderAppend(appendId, payWay);
      if (payWay === 1) {
        const mockModeEnabled = Boolean(prepayResult.config?.mock_mode);
        if (isWechatPayAlreadyPaid(prepayResult.config)) {
          await waitForWechatPayResultReady(() => orderApi.getPayResult(appendId, "orderAppend"));
        } else if (!mockModeEnabled && isWechatJsapiPayConfig(prepayResult.config)) {
          await invokeWechatJsapiPay(prepayResult.config);
          await waitForWechatPayResultReady(() => orderApi.getPayResult(appendId, "orderAppend"));
        } else {
          if (!mockModeEnabled) {
            throw new Error("当前环境未启用模拟微信支付");
          }
          const notifyResult = await orderApi.mockPayNotify(appendId, "success", payWay, "orderAppend");
          if (toNumber(notifyResult.pay_status) !== 1) {
            throw new Error(notifyResult.message || "模拟微信支付未完成");
          }
        }
      }

      const payResult = await orderApi.getPayResult(appendId, "orderAppend");
      if (toNumber(payResult.pay_status) !== 1) {
        throw new Error("加钟支付结果未确认，请稍后刷新重试");
      }

      await loadOrders();
      const detail = await orderApi.getOrderDetail(order.rawId);
      setDetailOrder(mapApiOrderToView(detail as OrderListItem));
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setActionError(getErrorMessage(error, "加钟支付失败，请稍后重试"));
    } finally {
      setActionLoading(false);
    }
  };

  if (detailOrder) {
    return (
      <>
        <OrderDetailPage
          order={detailOrder}
          actionError={actionError}
          actionLoading={actionLoading}
          payFeedback={payFeedback}
          onBack={() => {
            setDetailOrder(null);
            setActionError("");
            setPayFeedback(null);
          }}
          onCancelOrder={handleCancelOrder}
          onDeleteOrder={handleDeleteOrder}
          onPayOrder={handlePayOrder}
          onPayAppend={handlePayAppend}
          onPayPendingAppend={handlePayPendingAppend}
          onFinishEarly={handleFinishEarly}
          onOpenReview={handleOpenReview}
          onClosePayFeedback={() => setPayFeedback(null)}
        />
        {reviewOrder ? (
          <OrderReviewModal
            orderGoodsId={reviewOrder.orderGoodsId}
            therapistName={reviewOrder.therapist}
            therapistAvatar={reviewOrder.therapistAvatar}
            serviceName={reviewOrder.service}
            serviceImage={reviewOrder.image}
            onClose={() => setReviewOrder(null)}
            onSuccess={handleReviewSuccess}
          />
        ) : null}
      </>
    );
  }

  return (
    <div
      className="customer-page-shell flex min-h-screen flex-col"
      style={{ background: "#F2F2F5", overflowX: "clip" }}
    >
      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.4); }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .customer-orders-tabs::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <div
        className="sticky top-0 z-40 flex-shrink-0"
        style={{
          background: "linear-gradient(150deg, #FF9040 0%, #FF5500 100%)",
          paddingBottom: 0,
        }}
      >
        <div className="flex min-w-0 items-center justify-center gap-3 px-4 pb-2 pt-5">
          <div className="w-8 flex-shrink-0" />
          <h1 className="min-w-0 flex-1 truncate text-center text-base font-bold tracking-widest text-white">我的订单</h1>
          <div className="w-8 flex-shrink-0" />
        </div>

        {activeCount > 0 ? (
          <div className="flex justify-center px-4 pb-2">
            <div
              className="flex max-w-full items-center gap-1 rounded-full px-2.5 py-1"
              style={{ background: "rgba(255,255,255,0.2)" }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full bg-white flex-shrink-0"
                style={{ animation: "pulse-dot 1.5s infinite" }}
              />
              <span className="truncate text-xs font-semibold text-white">{activeCount} 单进行中</span>
            </div>
          </div>
        ) : null}

        <div
          className="customer-orders-tabs flex w-full items-stretch gap-1 overflow-x-auto px-2 pb-0"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
        >
          {TABS.map((tab) => {
            const active = activeTab === tab.key;
            const count = countByTab(orders, tab.key);
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="relative flex flex-shrink-0 cursor-pointer flex-col items-center justify-end whitespace-nowrap pb-3 pt-1"
                style={{ padding: "4px 16px 12px" }}
              >
                <div className="flex items-center gap-1">
                  <span
                    className="text-sm transition-all duration-200"
                    style={{
                      color: active ? "#fff" : "rgba(255,255,255,0.55)",
                      fontWeight: active ? 700 : 400,
                      fontSize: active ? 15 : 13,
                    }}
                  >
                    {tab.label}
                  </span>
                  {count > 0 && tab.key !== "all" ? (
                    <span
                      className="rounded-full px-1 text-[10px] font-bold leading-4"
                      style={{
                        background: active ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.15)",
                        color: active ? "#fff" : "rgba(255,255,255,0.6)",
                        minWidth: 16,
                        textAlign: "center",
                      }}
                    >
                      {count}
                    </span>
                  ) : null}
                </div>
                <span
                  className="absolute bottom-0 left-1/2 rounded-full transition-all duration-300"
                  style={{
                    height: 3,
                    width: active ? 20 : 0,
                    background: "#fff",
                    transform: "translateX(-50%)",
                    opacity: active ? 1 : 0,
                  }}
                />
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="flex flex-1 flex-col gap-3 overflow-y-auto px-3 pt-3"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)" }}
      >
        {pageError ? (
          <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}>
            {pageError}
          </div>
        ) : null}

        {loading ? (
          <div className="flex flex-1 flex-col items-center justify-center py-24">
            <div
              className="mb-4 h-10 w-10 animate-spin rounded-full border-4 border-t-transparent"
              style={{ borderColor: "#FF7B22 transparent #FF7B22 #FF7B22" }}
            />
            <p className="text-sm font-medium" style={{ color: "#BBBBBB" }}>
              订单加载中...
            </p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div
              className="mb-4 flex h-16 w-16 items-center justify-center rounded-full"
              style={{ background: "#FFF3EC" }}
            >
              <i className="ri-file-list-3-line text-3xl" style={{ color: "#FFB380" }} />
            </div>
            <p className="text-sm font-medium" style={{ color: "#BBBBBB" }}>
              暂无相关订单
            </p>
            <p className="mt-1 text-xs" style={{ color: "#CCCCCC" }}>
              快去预约一次服务吧
            </p>
          </div>
        ) : (
          filteredOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onDetail={(selected) => void openOrderDetail(selected)}
              onOpenReview={handleOpenReview}
            />
          ))
        )}
      </div>

      <BottomNav />
      {reviewOrder ? (
        <OrderReviewModal
          orderGoodsId={reviewOrder.orderGoodsId}
          therapistName={reviewOrder.therapist}
          therapistAvatar={reviewOrder.therapistAvatar}
          serviceName={reviewOrder.service}
          serviceImage={reviewOrder.image}
          onClose={() => setReviewOrder(null)}
          onSuccess={handleReviewSuccess}
        />
      ) : null}
    </div>
  );
}
